/**
 * SpinnerAtom.tsx
 *
 * A generic loading spinner atom wrapping PrimeReact's ProgressSpinner component
 * with design system styling and consistent API.
 */

import React from 'react';
import { useTranslation } from 'localization';
import { ProgressSpinner, ProgressSpinnerProps } from 'primereact/progressspinner';
import './SpinnerAtom.scss';

export type SpinnerSize = 'small' | 'normal' | 'large';
export type SpinnerVariant = 'default' | 'primary' | 'secondary';

export interface SpinnerAtomProps extends Omit<ProgressSpinnerProps, 'className' | 'style'> {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Visual variant of the spinner */
  variant?: SpinnerVariant;
  /** Additional CSS class names */
  className?: string;
  /** Optional label to display below the spinner */
  label?: string;
  /** Custom stroke width (default based on size) */
  strokeWidth?: string;
  /** Custom animation duration (default: '2s') */
  animationDuration?: string;
}

const SpinnerAtom: React.FC<SpinnerAtomProps> = ({
  size = 'normal',
  variant = 'default',
  className = '',
  label,
  strokeWidth,
  animationDuration = '2s',
  ...rest
}) => {
  const { t } = useTranslation();
  // Size mappings
  const sizeMap: Record<SpinnerSize, { width: string; height: string; stroke: string }> = {
    small: { width: '24px', height: '24px', stroke: '4' },
    normal: { width: '40px', height: '40px', stroke: '4' },
    large: { width: '64px', height: '64px', stroke: '3' },
  };

  const sizeConfig = sizeMap[size];

  // Build CSS classes
  const classes = [
    'spinner-atom',
    `spinner-atom--size-${size}`,
    `spinner-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className={classes}>
      {/* PrimeReact renders role="progressbar" and names it from nothing, so every spinner in
          the library ships an unnamed progressbar (axe aria-progressbar-name). The visible
          label names it when there is one; otherwise it announces as loading. */}
      <ProgressSpinner
        aria-label={label ?? t('common.loading')}
        style={{ width: sizeConfig.width, height: sizeConfig.height }}
        strokeWidth={strokeWidth || sizeConfig.stroke}
        animationDuration={animationDuration}
        {...rest}
      />
      {label && <span className="spinner-atom__label">{label}</span>}
    </div>
  );
};

SpinnerAtom.displayName = 'SpinnerAtom';

export default SpinnerAtom;
